import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

import {
  DataTableCellDefDirective,
  DataTableColumn,
  DataTableComponent,
  DataTableFilter,
  DataTableFilterChange,
  DataTableRowAction,
  DataTableSortState,
} from '../data-table';
import { Discount } from '../../../discount/models/discount.models';
import {
  discountListingStatusClass,
  discountStatusLabel,
  discountTypeBadgeClass,
  formatDiscountAmount,
  formatDiscountLocations,
  formatTimesApplied,
  resolveDiscountTypeBadge,
} from '../../../discount/utils/discount-display.util';

/**
 * Reusable listing table styled to match the Customers page table.
 * Currently configured for discount definitions; column templates live here.
 */
@Component({
  selector: 'app-listing-table',
  standalone: true,
  imports: [CommonModule, DataTableComponent, DataTableCellDefDirective],
  templateUrl: './listing-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./listing-table.component.scss'],
})
export class ListingTableComponent {
  @Input() discounts: Discount[] = [];
  @Input() sort: DataTableSortState = { column: 'created_at', direction: 'desc' };
  /** Maps column `key` → filter `field` (passed from the parent screen). */
  @Input() columnFilterMap: Record<string, string> = {};
  @Input() filters: DataTableFilter[] = [];

  @Output() sortChange = new EventEmitter<DataTableSortState>();
  @Output() viewDiscount = new EventEmitter<Discount>();
  @Output() editDiscount = new EventEmitter<Discount>();
  @Output() deleteDiscount = new EventEmitter<Discount>();
  @Output() filterChange = new EventEmitter<DataTableFilterChange>();

  readonly amount = formatDiscountAmount;
  readonly locations = formatDiscountLocations;
  readonly statusLabel = discountStatusLabel;
  readonly statusClass = discountListingStatusClass;
  readonly typeBadgeClass = discountTypeBadgeClass;
  readonly timesApplied = formatTimesApplied;
  readonly rowId = (discount: Discount): string => discount.id;

  private readonly baseColumns: DataTableColumn<Discount>[] = [
    {
      key: 'created_at',
      label: 'Created Date',
      sortable: true,
      minWidth: '195px',
      headerClass: 'created-column',
      cellClass: 'created-cell',
    },
    {
      key: 'name',
      label: 'Discount Name',
      sortable: true,
      minWidth: '210px',
      headerClass: 'name-column',
      cellClass: 'name-cell',
    },
    {
      key: 'type',
      label: 'Type',
      minWidth: '180px',
      headerClass: 'type-column',
      cellClass: 'type-cell',
    },
    {
      key: 'status',
      label: 'Status',
      minWidth: '130px',
      headerClass: 'status-column',
      cellClass: 'status-cell',
    },
    {
      key: 'locations',
      label: 'Location',
      minWidth: '210px',
      headerClass: 'location-column',
      cellClass: 'location-cell',
    },
    {
      key: 'discount_amount',
      label: 'Discount amount',
      minWidth: '150px',
      headerClass: 'amount-column',
      cellClass: 'amount-cell',
    },
    {
      key: 'times_applied',
      label: 'Times Applied',
      minWidth: '160px',
      headerClass: 'times-column',
      cellClass: 'times-cell',
    },
    {
      key: 'updated_at',
      label: 'Edited Date',
      sortable: true,
      minWidth: '195px',
      headerClass: 'updated-column',
      cellClass: 'updated-cell',
    },
  ];

  get columns(): DataTableColumn<Discount>[] {
    return this.baseColumns.map(column => ({
      ...column,
      filterField: this.columnFilterMap[column.key],
    }));
  }

  readonly rowActions: DataTableRowAction<Discount>[] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: 'assets/images/package/edit.svg',
      iconSize: 11,
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: 'assets/images/package/trash.svg',
      iconSize: 11,
      danger: true,
      borderTop: true,
    },
  ];

  typeBadgeLabel(discount: Discount): string {
    return resolveDiscountTypeBadge(discount).label;
  }

  onSortChange(sort: DataTableSortState): void {
    this.sortChange.emit(sort);
  }

  onFilterChange(change: DataTableFilterChange): void {
    this.filterChange.emit(change);
  }

  onRowAction(event: { action: string; row: Discount }): void {
    if (event.action === 'edit') {
      this.editDiscount.emit(event.row);
      return;
    }
    if (event.action === 'delete') {
      this.deleteDiscount.emit(event.row);
    }
  }

  trackById(_index: number, discount: Discount): string {
    return discount.id;
  }
}
